import { updateDesignByPrompt } from 'libs/e1api/design';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { DesignGenerationType, InfographicTemplateData } from '../DesignGenerator.types';
import { replaceIcons } from '../helpers/iconReplace';
import { replaceImages } from '../helpers/imageReplace';

type ProcessDesignTemplateArgs = {
  // Template data taken from config
  baseTemplateData: InfographicTemplateData;

  // Infograph that was processed by backend
  generatedInfograph: InfographState;

  // Keywords obtained from OpenAi - used for icon/image search and replace
  keywords?: string[];
};

/**
 * Processes a generated infograph for card templates.
 * Processing has 3 steps:
 * 1. Replace icons - as defined by replaceables
 * 2. Apply a randomly selected colour palette
 * 3. Replace images (TODO)
 *
 * @param templateId base template id
 * @param generatedInfograph
 * @param baseColor
 * @returns updated infograph
 */
export const processDesignTemplate = async ({
  baseTemplateData,
  generatedInfograph,
  keywords,
}: ProcessDesignTemplateArgs): Promise<InfographState> => {
  const { replaceables } = baseTemplateData;

  // Step 1: Replace Icons
  let updatedInfograph = generatedInfograph;
  if (replaceables?.icons?.length && keywords?.length) {
    updatedInfograph = await replaceIcons(updatedInfograph, replaceables.icons, keywords);
  }

  if (replaceables?.images?.length && keywords?.length) {
    updatedInfograph = await replaceImages(updatedInfograph, replaceables.images, keywords);
  }

  return updatedInfograph;
};

type generateDesignByAPIProps = {
  e2DesignId: WidgetId;
  userContext: string;
};
export const generateDesignByAPI = async ({
  e2DesignId,
  userContext,
}: generateDesignByAPIProps): Promise<InfographState> => {
  const { status, updatedInfographState, searchKeywords } = await updateDesignByPrompt(
    e2DesignId,
    userContext,
    DesignGenerationType.Infograph,
  );

  if (status !== 'ok') {
    throw new Error('Failed to update design by prompt');
  }

  const updatedWidgetsMap = updatedInfographState.widgets;
  const widgetIds = Object.keys(updatedWidgetsMap) as WidgetId[];
  const iconWidgets = widgetIds.filter((id) => {
    return !updatedWidgetsMap[id].isLocked && getWidgetTypeFromId(id) === WidgetType.Icon;
  });
  const imageWidgets = widgetIds.filter(
    (id) => !updatedWidgetsMap[id].isLocked && getWidgetTypeFromId(id) === WidgetType.Image,
  );
  const replaceables = {
    icons: iconWidgets,
    images: imageWidgets,
  };

  const baseInfographTemplateData = {
    e2DesignId: e2DesignId,
    replaceables,
  };

  let processedInfographState = await processDesignTemplate({
    baseTemplateData: baseInfographTemplateData,
    generatedInfograph: updatedInfographState,
    keywords: searchKeywords,
  });

  // Check widget order
  const widgetIdList = Object.keys(processedInfographState.widgets);
  const pageIdList = Object.keys(processedInfographState.pages);
  const invalidWidgetLayerOrder = processedInfographState.pages[pageIdList[0]].widgetLayerOrder.some(
    (id) => !widgetIdList.includes(id),
  );

  if (invalidWidgetLayerOrder) throw Error('Invalid widget layer order');

  return processedInfographState;
};
